import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Camera, History, PackagePlus, ShoppingBag, Trash2, X } from 'lucide-react';
import {
  createProduct, updateProduct, deleteProduct, setProductPhoto, deleteProductPhoto, Product,
} from '../../../services/productsApi';
import { getMovimientos, registrarEntrada, avisarVitrina, Movimiento } from '../vitrinaApi';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { useToast } from '../../../context/ToastContext';
import { money } from '../api';

const inputCls =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30 disabled:cursor-not-allowed disabled:opacity-60';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';
const NUEVA = '__nueva__';

/** Achica la foto en el navegador (lado mayor 800 px, jpeg): el backend topa en 1.5 MB. */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, 800 / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen')); };
    img.src = url;
  });
}

type Campos = {
  name: string; brand: string; category: string; description: string;
  price: string; cost: string; stock: string; stockMin: string; stockMid: string; stockMax: string;
  sku: string; notes: string; isActive: boolean;
};

function aCampos(p: Product | null): Campos {
  return {
    name: p?.name ?? '', brand: p?.brand ?? '', category: p?.category ?? '', description: p?.description ?? '',
    price: p ? String(p.price) : '', cost: p?.cost != null ? String(p.cost) : '',
    stock: p?.stock != null ? String(p.stock) : '',
    stockMin: p?.stockMin != null ? String(p.stockMin) : '', stockMid: p?.stockMid != null ? String(p.stockMid) : '', stockMax: p?.stockMax != null ? String(p.stockMax) : '',
    sku: p?.sku ?? '', notes: p?.notes ?? '',
    isActive: p ? p.isActive : true,
  };
}

const TIPO_MOV: Record<string, string> = {
  sale: 'Venta', credit: 'Fiado', return: 'Devolución', entry: 'Entrada', adjust: 'Ajuste a mano', count: 'Conteo',
};
const fechaCorta = (iso: string) => new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const soloNumero = (v: string) => v.replace(/[^\d.]/g, '');
const soloEntero = (v: string) => v.replace(/\D/g, '');

/** FICHA DE PRODUCTO — todo lo de un producto se cambia aquí y se guarda con un botón.
 *
 *  Por qué existe (Carlos, 29-sep-2026): el catálogo se editaba escribiendo encima de la tabla y
 *  nada lo decía; con la vitrina abierta nadie encontró cómo cambiar ni la foto. Y un precio que
 *  se edita en línea se mueve sin querer (FlowersMed, 27-jul: una venta salió $340 abajo).
 *
 *  `product` = null → alta. `readOnly` → quien no tiene permiso de catálogo la ve sin poder guardar. */
export default function ProductoFicha({ product, categorias, readOnly = false, inventario = false, puedeEntrada = true, onClose, onSaved }: {
  product: Product | null;
  categorias: string[];
  readOnly?: boolean;
  /** Inventario encendido en el estudio: aparecen «Llegó mercancía» y los movimientos. */
  inventario?: boolean;
  /** Puede anotar entradas (nivel `operate`), aunque no pueda cambiar el catálogo. */
  puedeEntrada?: boolean;
  onClose: () => void;
  onSaved: (mensaje: string) => void;
}) {
  const toast = useToast();
  const inicial = useMemo(() => aCampos(product), [product]);
  const [f, setF] = useState<Campos>(inicial);
  // Categoría: desplegable con las que ya existen + «Nueva categoría…», que cambia a campo de texto.
  const [catNueva, setCatNueva] = useState(false);
  // Foto: `fotoNueva` = dataURL elegida y aún sin subir · `quitarFoto` = borrar la actual al guardar.
  const [fotoNueva, setFotoNueva] = useState<string | null>(null);
  const [quitarFoto, setQuitarFoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirma, setConfirma] = useState<null | 'descartar' | 'eliminar'>(null);
  // Inventario: entrada de mercancía y movimientos del producto.
  const [entrada, setEntrada] = useState<null | { qty: string; note: string }>(null);
  const [movs, setMovs] = useState<Movimiento[] | null>(null);
  const [verMovs, setVerMovs] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const nombreRef = useRef<HTMLInputElement>(null);

  const set = <K extends keyof Campos>(k: K, v: Campos[K]) => setF((x) => ({ ...x, [k]: v }));
  const cambiados = (Object.keys(f) as (keyof Campos)[]).filter((k) => f[k] !== inicial[k]);
  const sucio = cambiados.length > 0 || fotoNueva != null || quitarFoto;
  const foto = fotoNueva ?? (quitarFoto ? null : product?.photoUrl ?? null);
  const precioCambio = !!product && f.price !== inicial.price && f.price !== '';

  const cerrar = () => { if (sucio && !readOnly) setConfirma('descartar'); else onClose(); };

  useEffect(() => { if (!product) nombreRef.current?.focus(); }, [product]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !confirma) cerrar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  async function elegirFoto(file: File | undefined) {
    if (!file) return;
    try { setFotoNueva(await fileToDataUrl(file)); setQuitarFoto(false); }
    catch (e: any) { toast.error(e?.message || 'No se pudo leer la imagen'); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  }

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (readOnly) return;
    const name = f.name.trim();
    if (!name) { toast.error('Ponle nombre al producto'); return; }
    const price = Number(f.price);
    if (f.price === '' || !(price >= 0)) { toast.error('Indica el precio'); return; }
    if (f.cost !== '' && !(Number(f.cost) >= 0)) { toast.error('Costo inválido'); return; }
    // Umbrales: mismo orden que exige el servidor, avisado antes de mandar.
    const um = (v: string) => (v === '' ? null : Number(v));
    const uMin = um(f.stockMin), uMid = um(f.stockMid), uMax = um(f.stockMax);
    if (uMin != null && uMid != null && uMid < uMin) { toast.error('El ámbar no puede ser menor que el rojo'); return; }
    if (uMax != null && ((uMid != null && uMax < uMid) || (uMin != null && uMax < uMin))) { toast.error('El tope de compra no puede ser menor que el ámbar ni que el rojo'); return; }
    setSaving(true);
    try {
      const texto = (v: string) => v.trim() || null;
      let id = product?.id;
      if (!id) {
        const r = await createProduct({
          name, price,
          brand: texto(f.brand) ?? undefined, category: texto(f.category) ?? undefined,
          description: texto(f.description) ?? undefined, notes: texto(f.notes) ?? undefined,
          sku: texto(f.sku) ?? undefined,
          cost: f.cost === '' ? undefined : Number(f.cost),
          stock: f.stock === '' ? undefined : Number(f.stock),
        });
        id = r.id;
        // El alta no admite umbrales ni «apagado»: van en un segundo paso.
        const extra: Parameters<typeof updateProduct>[1] = {};
        if (!f.isActive) extra.isActive = false;
        if (uMin != null) extra.stockMin = uMin;
        if (uMid != null) extra.stockMid = uMid;
        if (uMax != null) extra.stockMax = uMax;
        if (Object.keys(extra).length) await updateProduct(id, extra);
      } else if (cambiados.length) {
        // Solo viaja lo que cambió: dos personas editando campos distintos no se pisan.
        const patch: Parameters<typeof updateProduct>[1] = {};
        for (const k of cambiados) {
          if (k === 'name') patch.name = name;
          else if (k === 'price') patch.price = price;
          else if (k === 'cost') patch.cost = f.cost === '' ? null : Number(f.cost);
          else if (k === 'stock') patch.stock = f.stock === '' ? null : Number(f.stock);
          else if (k === 'stockMin' || k === 'stockMid' || k === 'stockMax') patch[k] = f[k] === '' ? null : Number(f[k]);
          else if (k === 'isActive') patch.isActive = f.isActive;
          else patch[k] = texto(f[k]);
        }
        await updateProduct(id, patch);
      }
      // La foto va aparte: si falla, lo demás ya quedó guardado y se dice cuál de las dos cosas falló.
      try {
        if (fotoNueva) await setProductPhoto(id, fotoNueva);
        else if (quitarFoto && product?.photoUrl) await deleteProductPhoto(id);
      } catch (err: any) {
        toast.error(`Los datos se guardaron, pero la foto no: ${err?.message || 'error al subirla'}`);
        onSaved('');
        return;
      }
      onSaved(product ? 'Producto guardado' : 'Producto agregado');
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo guardar');
    } finally { setSaving(false); }
  }

  async function sumarEntrada() {
    if (!product || !entrada) return;
    const n = Number(entrada.qty);
    if (!(n >= 1)) { toast.error('Indica cuántas piezas llegaron'); return; }
    setSaving(true);
    try {
      const r = await registrarEntrada(Number(product.id), n, entrada.note.trim() || undefined);
      avisarVitrina();
      onSaved(`Entrada anotada: +${n}${r.stock != null ? ` · ahora hay ${r.stock}` : ''}`);
    } catch (err: any) { toast.error(err?.message || 'No se pudo anotar la entrada'); }
    finally { setSaving(false); }
  }
  function abrirMovs() {
    setVerMovs((v) => !v);
    if (product && movs == null) getMovimientos(product.id).then((r) => setMovs(r.data)).catch(() => setMovs([]));
  }

  async function eliminar() {
    if (!product) return;
    try { await deleteProduct(product.id); onSaved('Producto eliminado'); }
    catch (err: any) { toast.error(err?.message || 'No se pudo eliminar'); }
  }

  const otrasCats = categorias.filter((c) => c !== f.category);

  // SOLO CONSULTA (Carlos 29-sep-2026: «no veo caso que aparezca eso de edición si de plano no lo van a
  // modificar»). Quien no puede cambiar el catálogo (cajeras, `vitrina: view`) ve una ficha limpia,
  // sin campos deshabilitados ni datos internos (costo, notas, topes de surtido).
  if (readOnly && !(inventario && puedeEntrada) && product) {   // con permiso de anotar entradas sigue la ficha completa
    const dato = (etq: string, val: ReactNode) => (
      <div><dt className={labelCls}>{etq}</dt><dd className="text-sm text-content">{val}</dd></div>
    );
    return createPortal(
      <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label={product.name}>
        <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
        <div data-testid="producto-consulta"
          className="modal-panel relative flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-xl">
          <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
            <h3 className="flex min-w-0 items-center gap-2 font-display text-lg font-bold text-content">
              <ShoppingBag size={18} className="shrink-0 text-brand-blue" /><span className="truncate">{product.name}</span>
            </h3>
            <button type="button" onClick={onClose} className="shrink-0 text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={20} /></button>
          </div>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
            <div className="mx-auto h-48 w-48 overflow-hidden rounded-2xl border border-line bg-surface-2">
              {product.photoUrl
                ? <img src={product.photoUrl} alt="" className="h-full w-full object-cover" />
                : <span className="flex h-full w-full flex-col items-center justify-center gap-1 text-content-3"><Camera size={30} /><span className="text-xs">Sin foto</span></span>}
            </div>
            <div className="text-center">
              <div className="font-display text-3xl font-bold tabular-nums text-content">{money(Number(product.price))}</div>
              {!product.isActive && <div className="mt-1 text-xs font-medium text-content-3">No se está vendiendo</div>}
            </div>
            <dl className="grid gap-3 sm:grid-cols-2">
              {product.brand && dato('Marca', product.brand)}
              {dato('Categoría', product.category || 'Sin categoría')}
              {inventario && product.stock != null && dato('Existencias', <span className="tabular-nums">{product.stock} pza{product.stock === 1 ? '' : 's'}</span>)}
              {product.sku && dato('Código', product.sku)}
            </dl>
            {product.description && <p className="whitespace-pre-line text-sm text-content-2">{product.description}</p>}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label={product ? `Editar ${product.name}` : 'Nuevo producto'}>
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={cerrar} />
      <form onSubmit={guardar} data-testid="producto-ficha"
        className="modal-panel relative flex h-full w-full max-w-xl flex-col border-l border-line bg-surface shadow-xl">
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <h3 className="flex min-w-0 items-center gap-2 font-display text-lg font-bold text-content">
            <ShoppingBag size={18} className="shrink-0 text-brand-blue" />
            <span className="truncate">{product ? (readOnly ? product.name : 'Editar producto') : 'Nuevo producto'}</span>
          </h3>
          <button type="button" onClick={cerrar} className="shrink-0 text-content-3 transition hover:text-content" aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {readOnly && (
            <p className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs text-content-2">
              Tu usuario puede vender, pero no cambiar el catálogo. Pídele el cambio a quien administra.
            </p>
          )}

          {/* FOTO */}
          <div className="flex items-center gap-4">
            <button type="button" disabled={readOnly} onClick={() => fileRef.current?.click()}
              title={foto ? 'Cambiar foto' : 'Agregar foto'}
              className="group relative h-32 w-32 shrink-0 overflow-hidden rounded-2xl border border-line bg-surface-2 transition enabled:hover:border-brand-blue">
              {foto ? (
                <img src={foto} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full flex-col items-center justify-center gap-1 text-content-3">
                  <Camera size={26} /><span className="text-xs">Sin foto</span>
                </span>
              )}
            </button>
            {!readOnly && (
              <div className="space-y-2">
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2">
                  <Camera size={15} /> {foto ? 'Cambiar foto' : 'Agregar foto'}
                </button>
                {foto && (
                  <button type="button" onClick={() => { setFotoNueva(null); setQuitarFoto(true); }}
                    className="block text-xs text-content-3 underline-offset-2 transition hover:text-red-500 hover:underline">
                    Quitar foto
                  </button>
                )}
                <p className="text-xs text-content-3">Se ve en la caja al vender.</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => elegirFoto(e.target.files?.[0])} />
          </div>

          {/* QUÉ ES */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="pf-name">Producto</label>
              <input id="pf-name" ref={nombreRef} disabled={readOnly} value={f.name} maxLength={160}
                onChange={(e) => set('name', e.target.value)} placeholder="Ej. Agua 600ml" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="pf-brand">Marca <span className="normal-case text-content-3">(opcional)</span></label>
              <input id="pf-brand" disabled={readOnly} value={f.brand} maxLength={80}
                onChange={(e) => set('brand', e.target.value)} placeholder="Ciel" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="pf-cat">Categoría</label>
              {catNueva ? (
                <div className="flex gap-2">
                  <input id="pf-cat" autoFocus value={f.category} maxLength={40}
                    onChange={(e) => set('category', e.target.value)} placeholder="Nombre de la categoría" className={inputCls} />
                  <button type="button" onClick={() => { setCatNueva(false); set('category', inicial.category); }}
                    className="shrink-0 rounded-lg border border-line px-2.5 text-xs text-content-2 transition hover:bg-surface-2">
                    Cancelar
                  </button>
                </div>
              ) : (
                <select id="pf-cat" disabled={readOnly} value={f.category}
                  onChange={(e) => {
                    if (e.target.value === NUEVA) { setCatNueva(true); set('category', ''); }
                    else set('category', e.target.value);
                  }} className={inputCls}>
                  <option value="">Sin categoría</option>
                  {f.category && <option value={f.category}>{f.category}</option>}
                  {otrasCats.map((c) => <option key={c} value={c}>{c}</option>)}
                  <option value={NUEVA}>＋ Nueva categoría…</option>
                </select>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="pf-desc">Descripción <span className="normal-case text-content-3">(opcional)</span></label>
              <textarea id="pf-desc" disabled={readOnly} value={f.description} maxLength={600} rows={3}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Sabor, talla, presentación… lo que ayude a reconocerlo al vender."
                className={`${inputCls} resize-y`} />
            </div>
          </div>

          {/* DINERO */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls} htmlFor="pf-price">Precio de venta</label>
              <input id="pf-price" disabled={readOnly} value={f.price} inputMode="decimal"
                onChange={(e) => set('price', soloNumero(e.target.value))} placeholder="25" className={`${inputCls} tabular-nums`} />
              {precioCambio && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Antes: ${inicial.price}</p>}
            </div>
            <div>
              <label className={labelCls} htmlFor="pf-cost">Costo <span className="normal-case text-content-3">(opcional)</span></label>
              <input id="pf-cost" disabled={readOnly} value={f.cost} inputMode="decimal"
                onChange={(e) => set('cost', soloNumero(e.target.value))} placeholder="Lo que te cuesta" className={`${inputCls} tabular-nums`} />
            </div>
          </div>

          {/* EXISTENCIAS */}
          <div>
            <label className={labelCls} htmlFor="pf-stock">Existencias</label>
            <input id="pf-stock" disabled={readOnly} value={f.stock} inputMode="numeric"
              onChange={(e) => set('stock', soloEntero(e.target.value))} placeholder="Sin control" className={`${inputCls} max-w-40 tabular-nums`} />
            <p className="mt-1 text-xs text-content-3">
              Cuántas piezas hay. Déjalo vacío si este producto no se cuenta.
              {inventario && product && ' Cambiarlo aquí queda anotado como ajuste a mano.'}
            </p>

            {inventario && (
              <div className="mt-3 rounded-xl border border-line p-3" data-testid="ficha-umbrales">
                <p className="text-xs font-medium uppercase tracking-wide text-content-3">Semáforo</p>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {([['stockMin', 'Rojo si hay ≤', 'bg-red-500'], ['stockMid', 'Ámbar si hay ≤', 'bg-amber-400'], ['stockMax', 'Tope de compra', 'bg-brand-blue']] as const).map(([k, l, dot]) => (
                    <div key={k}>
                      <label className="mb-1 flex items-center gap-1.5 text-xs text-content-3" htmlFor={`pf-${k}`}><span className={`h-2 w-2 rounded-full ${dot}`} />{l}</label>
                      <input id={`pf-${k}`} disabled={readOnly} value={f[k]} inputMode="numeric" placeholder="—"
                        onChange={(e) => set(k, soloEntero(e.target.value).slice(0, 6))} className={`${inputCls} tabular-nums`} />
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-content-3">Vacíos = sin semáforo (gris). El tope es hasta cuánto conviene surtir; «Qué comprar» calcula la diferencia.</p>
              </div>
            )}

            {inventario && product && (
              <div className="mt-3 space-y-2" data-testid="ficha-inventario">
                {puedeEntrada && (entrada ? (
                  <div className="rounded-xl border border-line bg-surface-2 p-3">
                    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-content-3">Llegó mercancía</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <input autoFocus value={entrada.qty} inputMode="numeric" placeholder="Piezas" data-testid="entrada-qty"
                        onChange={(e) => setEntrada({ ...entrada, qty: soloEntero(e.target.value).slice(0, 5) })}
                        className={`${inputCls} w-24 tabular-nums`} />
                      <input value={entrada.note} maxLength={120} placeholder="Nota (opcional)"
                        onChange={(e) => setEntrada({ ...entrada, note: e.target.value })}
                        className={`${inputCls} min-w-0 flex-1`} />
                      <button type="button" disabled={saving} onClick={sumarEntrada}
                        className="whitespace-nowrap rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line">
                        Sumar
                      </button>
                      <button type="button" onClick={() => setEntrada(null)} className="text-xs text-content-3 hover:text-content">Cancelar</button>
                    </div>
                    {sucio && <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">Tienes cambios sin guardar en la ficha: al sumar la entrada se cierra y esos cambios se pierden.</p>}
                  </div>
                ) : (
                  <button type="button" onClick={() => setEntrada({ qty: '', note: '' })}
                    className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2">
                    <PackagePlus size={15} /> Llegó mercancía
                  </button>
                ))}
                <button type="button" onClick={abrirMovs}
                  className="flex items-center gap-1.5 text-xs font-medium text-brand-blue underline-offset-2 hover:underline">
                  <History size={13} /> {verMovs ? 'Ocultar movimientos' : 'Ver movimientos'}
                </button>
                {verMovs && (
                  movs == null ? <p className="text-xs text-content-3">Cargando…</p>
                    : movs.length === 0 ? <p className="text-xs text-content-3">Todavía no hay movimientos de este producto.</p>
                      : (
                        <ul className="divide-y divide-line rounded-xl border border-line text-xs" data-testid="ficha-movimientos">
                          {movs.map((m) => (
                            <li key={m.id} className="flex items-center gap-2 px-3 py-1.5">
                              <span className="w-20 shrink-0 text-content-3">{fechaCorta(m.created_at)}</span>
                              <span className="min-w-0 flex-1 truncate text-content-2">
                                {TIPO_MOV[m.kind] || m.kind}{m.user_name ? ` · ${m.user_name}` : ''}{m.note ? ` · ${m.note}` : ''}
                              </span>
                              <span className={`w-10 shrink-0 text-right font-semibold tabular-nums ${m.qty < 0 ? 'text-content' : 'text-brand-blue'}`}>
                                {m.kind === 'count' && m.qty === 0 ? '=' : `${m.qty > 0 ? '+' : ''}${m.qty}`}
                              </span>
                              <span className="w-12 shrink-0 text-right tabular-nums text-content-3">{m.stock_after != null ? `→ ${m.stock_after}` : ''}</span>
                            </li>
                          ))}
                        </ul>
                      )
                )}
              </div>
            )}
          </div>

          {/* INTERNO */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="pf-sku">Código <span className="normal-case text-content-3">(opcional)</span></label>
              <input id="pf-sku" disabled={readOnly} value={f.sku} maxLength={60}
                onChange={(e) => set('sku', e.target.value)} placeholder="Código de barras o clave" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="pf-notes">Nota interna <span className="normal-case text-content-3">(opcional)</span></label>
              <input id="pf-notes" disabled={readOnly} value={f.notes} maxLength={255}
                onChange={(e) => set('notes', e.target.value)} placeholder="Dónde se guarda, proveedor…" className={inputCls} />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-content-2">
            <input type="checkbox" disabled={readOnly} checked={f.isActive} onChange={(e) => set('isActive', e.target.checked)}
              className="h-4 w-4 accent-[rgb(var(--c-accent-solid))]" />
            A la venta <span className="text-xs text-content-3">— apagado, deja de salir en la caja sin borrarse.</span>
          </label>
        </div>

        {!readOnly && (
          <div className="flex items-center gap-2 border-t border-line px-5 py-3">
            {product && (
              <button type="button" onClick={() => setConfirma('eliminar')}
                className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-2 text-sm text-content-3 transition hover:text-red-500">
                <Trash2 size={15} /> Eliminar
              </button>
            )}
            <div className="ml-auto flex gap-2">
              <button type="button" onClick={cerrar}
                className="whitespace-nowrap rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2 sm:px-3.5">
                Cancelar
              </button>
              <button type="submit" disabled={saving || (!!product && !sucio)}
                className="whitespace-nowrap rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3 sm:px-4">
                {saving ? 'Guardando…' : product ? 'Guardar' : 'Agregar'}
              </button>
            </div>
          </div>
        )}
      </form>

      {confirma === 'descartar' && (
        <ConfirmDialog title="¿Salir sin guardar?" lines={['Los cambios de este producto se van a perder.']}
          confirmLabel="Salir sin guardar" cancelLabel="Seguir editando"
          onConfirm={onClose} onClose={() => setConfirma(null)} />
      )}
      {confirma === 'eliminar' && product && (
        <ConfirmDialog title={`¿Eliminar «${product.name}»?`}
          lines={['Sale del catálogo y de la caja. Las ventas ya registradas no se tocan.']}
          warning="Si solo se acabó o ya no lo vas a vender por ahora, apaga «A la venta» en vez de eliminarlo."
          confirmLabel="Eliminar" onConfirm={eliminar} onClose={() => setConfirma(null)} />
      )}
    </div>, document.body);
}
